import {
  type ClinicalImpressionAssessmentValueCode,
  type ClinicalImpressionCode,
  ClinicalImpressionInvestigationCode,
  type ClinicalImpressionLocationCode,
  ClinicalImpressionNoteCode,
  type ClinicalImpressionPlanValueCode,
  ClinicalSummarySectionCode,
  type CreateReference,
  clinicalSummarySectionCodeDisplay,
  createCodeableConcept,
  createExtensionValue,
  ExtensionUrl,
  filterExtensions,
  findExtension,
  findExtensionValue,
  findResourceByType,
  getInvestigationReferencesByCode,
  isNotePinned,
  type WithId,
} from '@quromedical/fhir-common';
import type { FileStorageClient } from '@quromedical/storage';
import { compact } from '@quromedical/utils';
import type {
  Annotation,
  AuditEvent,
  ClinicalImpression,
  Encounter,
  Extension,
  Media,
  Observation,
  Reference,
} from 'fhir/r4';
import { mapEncounterToAdmissionWithId } from '../admission/mapping';
import {
  filterResourcesByType,
  findReferencedResource,
  findReferencedResources,
} from '../mappings';
import { mapFhirToModelMediaWithId } from '../media/mapping';
import type {
  CreateSOAPNote,
  Note,
  SOAPNote,
  StructuredContent,
} from '../notes/schema';
import { mapObservationsToLatestVitals } from '../observation/mapping';
import { type ResourceNote, ResourceNoteSchema } from '../resource-note';

const createAnnotation = (
  code: ClinicalImpressionNoteCode,
  text: string,
): Annotation => ({
  text,
  extension: [
    {
      url: ExtensionUrl.clinicalImpressionNoteCode,
      valueCode: code,
    },
  ],
});

const createStructuredContentJSONExtension = createExtensionValue(
  ExtensionUrl.clinicalImpressionStructuredContentJSON,
);

const createNotePinnedExtension = createExtensionValue(
  ExtensionUrl.clinicalImpressionPinned,
);

const createStructuredContentExtension = (
  structuredContent: StructuredContent,
): fhir4.Extension =>
  createStructuredContentJSONExtension(JSON.stringify(structuredContent));

export const mapNoteToClinicalImpression = (
  soapNote: CreateSOAPNote,
  code: ClinicalImpressionCode,
  locationCode: ClinicalImpressionLocationCode,
  subject: Reference,
  practitioner: Reference,
  practitionersToNotify: Reference[],
  effectiveDateTime: string,
  pinned: boolean,
  encounter?: CreateReference,
  structuredContent?: StructuredContent,
): ClinicalImpression => {
  const note = [
    createAnnotation(
      ClinicalImpressionNoteCode.Subjective,
      soapNote.subjective,
    ),
    soapNote.objective &&
      createAnnotation(
        ClinicalImpressionNoteCode.Objective,
        soapNote.objective,
      ),
    soapNote.assessment &&
      createAnnotation(
        ClinicalImpressionNoteCode.Assessment,
        soapNote.assessment,
      ),
    soapNote.plan &&
      createAnnotation(ClinicalImpressionNoteCode.Plan, soapNote.plan),
  ];

  const notificationExtensions = practitionersToNotify.map<Extension>(
    (ref) => ({
      url: ExtensionUrl.practitionerToNotify,
      valueReference: ref,
    }),
  );

  const structuredContentExtension =
    structuredContent && createStructuredContentExtension(structuredContent);

  return {
    extension: compact([
      {
        url: ExtensionUrl.clinicalImpressionLocationCode,
        valueCode: locationCode,
      },
      {
        url: ExtensionUrl.clinicalImpressionAssessmentCode,
        valueCode: soapNote.assessmentCode,
      },
      {
        url: ExtensionUrl.clinicalImpressionPlanCode,
        valueCode: soapNote.planCode,
      },
      ...notificationExtensions,
      createNotePinnedExtension(pinned),
      structuredContentExtension,
    ]),
    resourceType: 'ClinicalImpression',
    status: 'completed',
    subject,
    assessor: practitioner,
    effectiveDateTime,
    date: effectiveDateTime,
    code: createCodeableConcept(code),
    note: compact(note),
    encounter,
  };
};

const findSubjectiveNotes = (
  noteCode: ClinicalImpressionNoteCode,
  notes: Annotation[],
): string[] | undefined => {
  const subjective = notes.filter((annotation) => {
    const extension = findExtension(
      ExtensionUrl.clinicalImpressionNoteCode,
      annotation.extension,
    );

    return extension?.valueCode === noteCode;
  });

  return subjective.map((note) => note.text);
};

const findNote = (
  noteCode: ClinicalImpressionNoteCode,
  notes: Annotation[],
): string | undefined => {
  const note = notes.find((annotation) => {
    const extension = findExtension(
      ExtensionUrl.clinicalImpressionNoteCode,
      annotation.extension,
    );

    return extension?.valueCode === noteCode;
  });

  return note?.text;
};

const isStructuredContent = (data: unknown): data is StructuredContent =>
  (data as StructuredContent)?.type === 'nested-comment-nodes';

const parseStructuredContent = (
  extension: fhir4.Extension[] = [],
): StructuredContent | undefined => {
  const json = findExtensionValue(
    ExtensionUrl.clinicalImpressionStructuredContentJSON,
  )(extension);

  if (!json) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(json);

    const isValid = isStructuredContent(parsed);

    if (!isValid) {
      return undefined;
    }

    return parsed;
  } catch (err) {
    console.error('error parsing structured note data', err);

    return undefined;
  }
};

/**
 * Get the associated resources for a note and build a single note resource from them based on the
 * provided `clinicalImpression`
 *
 * @param clinicalImpression The core ClinicalImpression that represents the note
 * @param allMedia A list of media. The relevant media elements will be extracted and included
 * @param observation A list of observations. The relevant elements will be extracted and included
 */
export const mergeResourcesToNote = (
  clinicalImpression: WithId<ClinicalImpression>,
  allMedia: WithId<Media>[],
  observations: WithId<Observation>[],
  encounters: WithId<Encounter>[],
  storageClient: FileStorageClient,
): Note => {
  const {
    id,
    assessor,
    investigation = [],
    extension = [],
    note: clinicalImpressionNote = [],
    code: clinicalImpressionCode,
    effectiveDateTime: dateCreated,
  } = clinicalImpression;

  const noteCode = clinicalImpressionCode?.text as
    | ClinicalImpressionCode
    | undefined;

  const locationCode = findExtension(
    ExtensionUrl.clinicalImpressionLocationCode,
    extension,
  )?.valueCode as ClinicalImpressionLocationCode | undefined;

  const practitionersToNotify = filterExtensions(
    ExtensionUrl.practitionerToNotify,
    extension,
  ).map((ext) => ext.valueReference);

  const soapNote: SOAPNote = {
    subjective: findSubjectiveNotes(
      ClinicalImpressionNoteCode.Subjective,
      clinicalImpressionNote,
    ),
    objective: findNote(
      ClinicalImpressionNoteCode.Objective,
      clinicalImpressionNote,
    ),
    assessment: findNote(
      ClinicalImpressionNoteCode.Assessment,
      clinicalImpressionNote,
    ),
    plan: findNote(ClinicalImpressionNoteCode.Plan, clinicalImpressionNote),
    assessmentCode: findExtension(
      ExtensionUrl.clinicalImpressionAssessmentCode,
      extension,
    )?.valueCode as ClinicalImpressionAssessmentValueCode | undefined,
    planCode: findExtension(ExtensionUrl.clinicalImpressionPlanCode, extension)
      ?.valueCode as ClinicalImpressionPlanValueCode | undefined,
    dailySummary: findNote(
      ClinicalImpressionNoteCode.DailySummary,
      clinicalImpressionNote,
    ),
    overallSummary: findNote(
      ClinicalImpressionNoteCode.OverallSummary,
      clinicalImpressionNote,
    ),
  };

  const mediaReferences = getInvestigationReferencesByCode(
    ClinicalImpressionInvestigationCode.Media,
    'Media',
    investigation,
  );

  const vitalsReferences = getInvestigationReferencesByCode(
    ClinicalImpressionInvestigationCode.LatestVitals,
    'Observation',
    investigation,
  );

  const referencedMedia = findReferencedResources(mediaReferences, allMedia);
  const referencedObservations = findReferencedResources(
    vitalsReferences,
    observations,
  );
  const referencedEncounter = findReferencedResource(
    clinicalImpression.encounter,
    encounters,
  );

  const media = referencedMedia.map((resource) =>
    mapFhirToModelMediaWithId(storageClient, resource),
  );

  const vitals = mapObservationsToLatestVitals(referencedObservations);
  const pinned = isNotePinned(clinicalImpression);
  const admission =
    referencedEncounter && mapEncounterToAdmissionWithId(referencedEncounter);

  const structuredContent = parseStructuredContent(extension);

  return {
    id,
    assessor,
    media,
    locationCode,
    vitals,
    note: soapNote,
    code: noteCode,
    practitionersToNotify: compact(practitionersToNotify),
    dateCreated,
    pinned,
    admission,
    structuredContent,
  };
};

/**
 * Get the associated resources for a note and build a single note resource from them based on the
 * provided `clinicalImpression`
 *
 * @param clinicalImpression The core ClinicalImpression that represents the note
 * @param allMedia A list of media. The relevant media elements will be extracted and included
 * @param observation A list of observations. The relevant elements will be extracted and included
 */
export const mapGenericClinicalImpressionToResourceNote = (
  clinicalImpression: WithId<ClinicalImpression>,
  organization: CreateReference,
): ResourceNote => {
  const {
    assessor,
    note: clinicalImpressionNote = [],

    effectiveDateTime,
  } = clinicalImpression;

  const resourceNote = {
    author: assessor || organization,
    text: clinicalImpressionNote.map((note) => note.text).join('\n'),
    time: effectiveDateTime,
  };

  const result = ResourceNoteSchema.parse(resourceNote);
  return result;
};

/**
 * Maps an overall summary clinical impression to a resource note with structured sections
 *
 * @param clinicalImpression The overall summary ClinicalImpression
 * @param organization Default organization reference if assessor is not available
 */
export const mapOverallSummaryClinicalImpressionToResourceNote = (
  clinicalImpression: WithId<ClinicalImpression>,
  organization: CreateReference,
): ResourceNote => {
  const { note: clinicalImpressionNote = [], effectiveDateTime } =
    clinicalImpression;

  // Group notes by section code for better organization
  const sectionNotes: Partial<Record<ClinicalSummarySectionCode, string[]>> =
    {};
  const generalNotes: string[] = [];

  clinicalImpressionNote.forEach((note) => {
    if (note.extension) {
      const sectionCode = findExtensionValue(
        ExtensionUrl.clinicalImpressionNoteCode,
      )(note.extension) as ClinicalSummarySectionCode | undefined;

      // If the note has a section coding, group it by that code
      if (sectionCode) {
        if (!sectionNotes[sectionCode]) {
          sectionNotes[sectionCode] = [];
        }

        sectionNotes[sectionCode].push(note.text);
      } else {
        // Notes without section coding
        generalNotes.push(note.text);
      }
    }
  });

  // Build structured text with section headers
  const structuredSections: string[] = [];

  // Add sections in a logical clinical order
  const sectionOrder = [
    {
      code: ClinicalSummarySectionCode.HistoryAndExamination,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.HistoryAndExamination
        ],
    },
    {
      code: ClinicalSummarySectionCode.Investigations,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.Investigations
        ],
    },
    {
      code: ClinicalSummarySectionCode.Diagnoses,
      title:
        clinicalSummarySectionCodeDisplay[ClinicalSummarySectionCode.Diagnoses],
    },
    {
      code: ClinicalSummarySectionCode.Management,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.Management
        ],
    },
    {
      code: ClinicalSummarySectionCode.Complications,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.Complications
        ],
    },
    {
      code: ClinicalSummarySectionCode.HospitalEscalations,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.HospitalEscalations
        ],
    },
    {
      code: ClinicalSummarySectionCode.Procedures,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.Procedures
        ],
    },
    {
      code: ClinicalSummarySectionCode.FutureManagement,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.FutureManagement
        ],
    },
    {
      code: ClinicalSummarySectionCode.MedicationChanges,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.MedicationChanges
        ],
    },
    {
      code: ClinicalSummarySectionCode.MedicationsToTakeHome,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.MedicationsToTakeHome
        ],
    },
    {
      code: ClinicalSummarySectionCode.AllergiesAndAdverseReactions,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.AllergiesAndAdverseReactions
        ],
    },
    {
      code: ClinicalSummarySectionCode.InformationForPatient,
      title:
        clinicalSummarySectionCodeDisplay[
          ClinicalSummarySectionCode.InformationForPatient
        ],
    },
  ];

  sectionOrder.forEach(({ code, title }) => {
    const sectionContent = sectionNotes[code];
    if (sectionContent && sectionContent.length > 0) {
      const content = sectionContent.join('\n');
      structuredSections.push(`**${title}**\n${content}`);
    }
  });

  // Add general notes without section headers
  if (generalNotes.length > 0) {
    structuredSections.push(generalNotes.join('\n'));
  }

  // Combine all sections
  const text = structuredSections.join('\r\n\r\n');

  const resourceNote = {
    author: organization,
    text,
    time: effectiveDateTime,
  };

  const result = ResourceNoteSchema.parse(resourceNote);
  return result;
};

export type NoteCreationEntry =
  | AuditEvent
  | ClinicalImpression
  | Media
  | Observation
  | Encounter;

/**
 * Will get all observations and media found and use the relevant ones to create a Note
 *
 * Assumes that there is only one ClinicalImpression in the list of resources and will use the first
 * one found.
 */
export const mapCreationEntriesToNote = (
  storageClient: FileStorageClient,
  entries: NoteCreationEntry[],
): Note | undefined => {
  const clinicalImpression = findResourceByType<WithId<ClinicalImpression>>(
    'ClinicalImpression',
    entries,
  );
  const observations = filterResourcesByType<WithId<Observation>>(
    'Observation',
    entries,
  );
  const media = filterResourcesByType<WithId<Media>>('Media', entries);
  const encounters = filterResourcesByType<WithId<Encounter>>(
    'Encounter',
    entries,
  );

  if (!clinicalImpression) {
    return undefined;
  }

  return mergeResourcesToNote(
    clinicalImpression,
    media,
    observations,
    encounters,
    storageClient,
  );
};
